import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  ClockIcon,
  ExternalLinkIcon,
  HashIcon,
  MailIcon,
  PackageIcon,
  ReceiptIcon,
  TagIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Skeleton } from '@/components/skeleton/skeleton';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { getOrderQueryOptions } from '@/features/orders/api/get-order.api';
import type { Order, OrderItem } from '@/features/orders/types/order.type';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

function formatRupiah(amount: number | null | undefined) {
  if (amount == null) return '-';
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

function statusBadgeVariant(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'paid' || normalized === 'completed') {
    return 'success' as const;
  }
  if (normalized === 'pending' || normalized === 'processing') {
    return 'warning' as const;
  }
  if (
    normalized === 'expired' ||
    normalized === 'failed' ||
    normalized === 'cancelled'
  ) {
    return 'destructive' as const;
  }
  return 'info' as const;
}

function typeBadgeVariant(type: string) {
  const normalized = type.toLowerCase();
  if (normalized === 'product_digital') return 'info' as const;
  if (normalized === 'product_event') return 'mentor' as const;
  if (normalized === 'product_tryout') return 'warning' as const;
  if (normalized === 'mentoring') return 'mentor' as const;
  return 'secondary' as const;
}

function formatType(type: string) {
  return type.replace(/_/g, ' ');
}

export interface DetailOrderDialogProps extends BaseDialogProps {
  orderId: string;
}

export default function DetailOrderDialog({
  dialog,
  orderId,
}: DetailOrderDialogProps) {
  const { closeDetailOrderDialog } = useV2DialogContext();

  const getOrderQuery = useQuery(getOrderQueryOptions({ order_id: orderId }));

  const order = getOrderQuery.data?.result;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailOrderDialog();
        }
      }}
    >
      <DialogContent className="sm:max-w-160 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detail Order</DialogTitle>
          <DialogDescription>
            Informasi lengkap tentang order ini.
          </DialogDescription>
        </DialogHeader>

        {getOrderQuery.isPending ? (
          <DetailOrderSkeleton />
        ) : getOrderQuery.isError || !order ? (
          <p className="text-sm text-destructive">
            Gagal memuat data order. Silakan coba lagi.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            <DetailOrderHeader order={order} />
            <DetailOrderParties order={order} />
            <DetailOrderInfo order={order} />
            {order.items.length > 0 && <DetailOrderItems items={order.items} />}
            {order.payment_payload?.url && <DetailOrderPayment order={order} />}
            <DetailOrderMeta order={order} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DetailOrderHeaderProps {
  order: Order;
}

function DetailOrderHeader({ order }: DetailOrderHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex flex-col gap-1.5">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <HashIcon className="size-3" />
          Invoice
        </p>
        <p className="text-base font-semibold text-foreground">
          {order.invoice_number}
        </p>
        <p className="text-2xl font-semibold tracking-tight text-foreground">
          {formatRupiah(order.amount)}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <Badge variant={statusBadgeVariant(order.status)}>{order.status}</Badge>
        <Badge variant={typeBadgeVariant(order.type)} className="capitalize">
          {formatType(order.type)}
        </Badge>
      </div>
    </div>
  );
}

interface DetailOrderPartiesProps {
  order: Order;
}

function DetailOrderParties({ order }: DetailOrderPartiesProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <PartyCard
        icon={<UserIcon className="size-4" />}
        label="User"
        name={order.user?.name ?? '-'}
        email={order.user?.email ?? undefined}
      />
      {order.mentor && (
        <PartyCard
          icon={<UsersIcon className="size-4" />}
          label="Mentor"
          name={order.mentor.name ?? '-'}
          email={order.mentor.email ?? undefined}
        />
      )}
    </div>
  );
}

interface PartyCardProps {
  icon: React.ReactNode;
  label: string;
  name: string;
  email?: string;
}

function PartyCard({ icon, label, name, email }: PartyCardProps) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <p className="text-sm font-medium text-foreground">{name}</p>
      {email && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MailIcon className="size-3" />
          {email}
        </p>
      )}
    </div>
  );
}

interface DetailOrderInfoProps {
  order: Order;
}

function DetailOrderInfo({ order }: DetailOrderInfoProps) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
      {order.topic_title?.name && (
        <InfoRow
          icon={<TagIcon className="size-4" />}
          label="Topik"
          value={order.topic_title.name}
        />
      )}
      {order.schedule?.name && (
        <InfoRow
          icon={<CalendarIcon className="size-4" />}
          label="Jadwal"
          value={order.schedule.name}
        />
      )}
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibayar Pada"
        value={formatDate(order.paid_at)}
      />
      <InfoRow
        icon={<ClockIcon className="size-4" />}
        label="Kedaluwarsa Pada"
        value={formatDate(order.expired_at)}
      />
    </div>
  );
}

interface DetailOrderItemsProps {
  items: OrderItem[];
}

function DetailOrderItems({ items }: DetailOrderItemsProps) {
  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <PackageIcon className="size-3.5" />
        Item Pesanan
      </p>
      <div className="flex flex-col gap-2">
        {items.map(item => (
          <div
            key={item.id}
            className="flex flex-col gap-2 rounded-lg border border-border p-3 transition-colors hover:bg-muted/30"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">
                  {item.metadata?.product_title ??
                    item.metadata?.topic_title_name ??
                    item.item_type}
                </p>
                <p className="text-xs text-muted-foreground">
                  Qty {item.qty} · {formatRupiah(item.base_price)}
                </p>
              </div>
              <p className="shrink-0 text-sm font-semibold text-foreground">
                {formatRupiah(item.total)}
              </p>
            </div>
            {(item.discounted_price > 0 || item.tax_price > 0) && (
              <div className="flex flex-wrap gap-2 border-t border-border pt-2 text-xs text-muted-foreground">
                {item.discounted_price > 0 && (
                  <span>Diskon: {formatRupiah(item.discounted_price)}</span>
                )}
                {item.tax_price > 0 && (
                  <span>Pajak: {formatRupiah(item.tax_price)}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface DetailOrderPaymentProps {
  order: Order;
}

function DetailOrderPayment({ order }: DetailOrderPaymentProps) {
  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <ReceiptIcon className="size-3.5" />
        Pembayaran
      </p>
      {order.payment_payload?.url && (
        <a
          href={order.payment_payload.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
        >
          <ExternalLinkIcon className="size-3.5" />
          Buka tautan pembayaran
        </a>
      )}
    </div>
  );
}

interface DetailOrderMetaProps {
  order: Order;
}

function DetailOrderMeta({ order }: DetailOrderMetaProps) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibuat Pada"
        value={formatDate(order.created_at)}
      />
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}

function DetailOrderSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-7 w-32" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
    </div>
  );
}
